import { useCallback, useEffect, useMemo, useState } from "react";
import type { FieldError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../hooks/use-debounce";
import { cn, interval } from "../lib/utils";
import { Badge } from "./badge";
import { Checkbox } from "./checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./command";
import { InlineFeedback, InlineFeedbackGroup } from "./inline-feedback";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
  type FooterRenderer,
  type LoadingRenderer,
  SearchableSelectTriggerButton,
} from "./searchable-select";
import { Skeleton } from "./skeleton";
import { TextField } from "./textfield";
import { TruncatedText } from "./truncated-text";
import { Typography } from "./typography";

export interface TreeOption {
  value: string;
  label: string;
  description?: string;
}

export interface TreeSection {
  id: string;
  title: string;
  options: TreeOption[];
}

export interface TreeValue {
  selectedSectionIds: string[];
  selectedOptionValues: string[];
}

export interface TreeSearchableSelectProps {
  sections: TreeSection[];
  value?: TreeValue;
  defaultValue?: TreeValue;
  onSelect?: (value: TreeValue) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  noResultsText?: string;
  error?: FieldError;
  helperText?: string;
  variant?: "default" | "ghost";
  size?: "default" | "small";
  isLoading?: boolean;
  onOpenChange?: (open: boolean) => void;
  loadingRenderer?: LoadingRenderer;
  footerRenderer?: FooterRenderer;
  readonly?: boolean;
}

const SKELETON_ITEM_COUNT = 5;

// Default loading renderer - shows skeleton items like SearchableSelect
const DefaultLoadingRenderer: LoadingRenderer = () => {
  return (
    <>
      {interval(SKELETON_ITEM_COUNT).map((index) => (
        <CommandItem key={`loading-${index}`} disabled>
          <div className="flex items-center w-full py-1">
            <Skeleton className="h-4 w-full rounded" />
          </div>
        </CommandItem>
      ))}
    </>
  );
};

const _TreeSearchableSelect = (
  props: TreeSearchableSelectProps & {
    ref?: React.Ref<HTMLButtonElement>;
  },
) => {
  const { t } = useTranslation();
  const {
    sections,
    value,
    defaultValue = { selectedSectionIds: [], selectedOptionValues: [] },
    onSelect,
    placeholder = t("select_placeholder"),
    searchPlaceholder = t("search_in_list_placeholder"),
    className = "",
    noResultsText = t("no_items"),
    error,
    helperText,
    variant = "default",
    size = "default",
    isLoading = false,
    onOpenChange,
    loadingRenderer: LoadingRenderer = DefaultLoadingRenderer,
    footerRenderer: FooterRenderer = () => <></>,
    readonly,
    ref,
  } = props;

  const [internalValue, setInternalValue] = useState<TreeValue>(
    value ?? defaultValue,
  );
  const [searchValue, setSearchValue] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);

  const debouncedSearchValue = useDebounce(searchValue, 300);

  // Sync external value with internal state
  useEffect(() => {
    if (value) {
      setInternalValue(value);
    }
  }, [value]);

  // Get all option values in a section
  const getSectionOptionValues = useCallback(
    (sectionId: string): string[] => {
      const section = sections.find((s) => s.id === sectionId);
      return section?.options.map((opt) => opt.value) ?? [];
    },
    [sections],
  );

  // Check if all options in a section are selected
  const areAllOptionsInSectionSelected = useCallback(
    (sectionId: string, selectedOptionValues: string[]): boolean => {
      const sectionOptions = getSectionOptionValues(sectionId);
      if (sectionOptions.length === 0) return false;
      return sectionOptions.every((optValue) =>
        selectedOptionValues.includes(optValue),
      );
    },
    [getSectionOptionValues],
  );

  // Check if section is fully selected (either via section ID or all options)
  const isSectionFullySelected = useCallback(
    (sectionId: string): boolean => {
      return (
        internalValue.selectedSectionIds.includes(sectionId) ||
        areAllOptionsInSectionSelected(
          sectionId,
          internalValue.selectedOptionValues,
        )
      );
    },
    [
      internalValue.selectedSectionIds,
      internalValue.selectedOptionValues,
      areAllOptionsInSectionSelected,
    ],
  );

  // Check if section is partially selected (some but not all options)
  const isSectionPartiallySelected = useCallback(
    (sectionId: string): boolean => {
      if (internalValue.selectedSectionIds.includes(sectionId)) return false;

      const sectionOptions = getSectionOptionValues(sectionId);
      const selectedInSection = sectionOptions.filter((optValue) =>
        internalValue.selectedOptionValues.includes(optValue),
      );

      return (
        selectedInSection.length > 0 &&
        selectedInSection.length < sectionOptions.length
      );
    },
    [
      internalValue.selectedSectionIds,
      internalValue.selectedOptionValues,
      getSectionOptionValues,
    ],
  );

  // Get checkbox state for section: true | false | "indeterminate"
  const getSectionCheckboxState = useCallback(
    (sectionId: string): boolean | "indeterminate" => {
      if (isSectionFullySelected(sectionId)) return true;
      if (isSectionPartiallySelected(sectionId)) return "indeterminate";
      return false;
    },
    [isSectionFullySelected, isSectionPartiallySelected],
  );

  // Check if individual option is selected
  const isOptionSelected = useCallback(
    (sectionId: string, optionValue: string): boolean => {
      // If whole section is selected, option is selected
      if (internalValue.selectedSectionIds.includes(sectionId)) return true;
      // Otherwise check if option is individually selected
      return internalValue.selectedOptionValues.includes(optionValue);
    },
    [internalValue.selectedSectionIds, internalValue.selectedOptionValues],
  );

  // Handle section checkbox toggle
  const handleSectionToggle = useCallback(
    (sectionId: string) => {
      const isFullySelected = isSectionFullySelected(sectionId);
      const sectionOptions = getSectionOptionValues(sectionId);

      let newSelectedSectionIds: string[];
      let newSelectedOptionValues: string[];

      if (isFullySelected) {
        // Deselect entire section
        newSelectedSectionIds = internalValue.selectedSectionIds.filter(
          (id) => id !== sectionId,
        );
        // Remove all options from this section
        newSelectedOptionValues = internalValue.selectedOptionValues.filter(
          (optValue) => !sectionOptions.includes(optValue),
        );
      } else {
        // Select entire section
        newSelectedSectionIds = [
          ...internalValue.selectedSectionIds,
          sectionId,
        ];
        // Remove individual options from this section (they're now covered by section selection)
        newSelectedOptionValues = internalValue.selectedOptionValues.filter(
          (optValue) => !sectionOptions.includes(optValue),
        );
      }

      const newValue: TreeValue = {
        selectedSectionIds: newSelectedSectionIds,
        selectedOptionValues: newSelectedOptionValues,
      };

      setInternalValue(newValue);
      onSelect?.(newValue);
    },
    [isSectionFullySelected, getSectionOptionValues, internalValue, onSelect],
  );

  // Handle individual option toggle
  const handleOptionToggle = useCallback(
    (sectionId: string, optionValue: string) => {
      const isSelected = isOptionSelected(sectionId, optionValue);
      const sectionOptions = getSectionOptionValues(sectionId);

      let newSelectedSectionIds = [...internalValue.selectedSectionIds];
      let newSelectedOptionValues = [...internalValue.selectedOptionValues];

      // If section was selected, we need to expand it to individual options
      if (newSelectedSectionIds.includes(sectionId)) {
        newSelectedSectionIds = newSelectedSectionIds.filter(
          (id) => id !== sectionId,
        );
        // Add all options except the one being toggled off
        const otherOptions = sectionOptions.filter(
          (opt) => opt !== optionValue,
        );
        newSelectedOptionValues = [...newSelectedOptionValues, ...otherOptions];
      } else {
        // Normal toggle of individual option
        if (isSelected) {
          newSelectedOptionValues = newSelectedOptionValues.filter(
            (val) => val !== optionValue,
          );
        } else {
          newSelectedOptionValues = [...newSelectedOptionValues, optionValue];

          // Check if all options in section are now selected → collapse to section
          const allOptionsSelected = sectionOptions.every(
            (opt) =>
              opt === optionValue || newSelectedOptionValues.includes(opt),
          );
          if (allOptionsSelected) {
            newSelectedSectionIds = [...newSelectedSectionIds, sectionId];
            // Remove individual options for this section
            newSelectedOptionValues = newSelectedOptionValues.filter(
              (val) => !sectionOptions.includes(val),
            );
          }
        }
      }

      const newValue: TreeValue = {
        selectedSectionIds: newSelectedSectionIds,
        selectedOptionValues: newSelectedOptionValues,
      };

      setInternalValue(newValue);
      onSelect?.(newValue);
    },
    [isOptionSelected, getSectionOptionValues, internalValue, onSelect],
  );

  // Filter sections based on search
  const filteredSections = useMemo(() => {
    if (!debouncedSearchValue) return sections;

    const searchLower = debouncedSearchValue.toLowerCase();

    return sections
      .map((section) => {
        // Check if section title matches
        const sectionMatches = section.title
          .toLowerCase()
          .includes(searchLower);

        // Filter options that match
        const matchingOptions = section.options.filter(
          (option) =>
            option.label.toLowerCase().includes(searchLower) ||
            option.description?.toLowerCase().includes(searchLower),
        );

        // Include section if title matches (with all options) or if any option matches
        if (sectionMatches) {
          return section;
        }
        if (matchingOptions.length > 0) {
          return { ...section, options: matchingOptions };
        }
        return null;
      })
      .filter((section): section is TreeSection => section !== null);
  }, [sections, debouncedSearchValue]);

  // Calculate display value for trigger
  const triggerDisplayValue = useMemo(() => {
    // Calculate total options by expanding sections
    const optionsFromSections = internalValue.selectedSectionIds.reduce(
      (sum, sectionId) => {
        const section = sections.find((s) => s.id === sectionId);
        return sum + (section?.options.length ?? 0);
      },
      0,
    );

    const totalOptionsCount =
      optionsFromSections + internalValue.selectedOptionValues.length;

    if (totalOptionsCount === 0) return "";

    return (
      <Badge className="text-gray-700" variant="tag">
        {t("selected_options", { count: totalOptionsCount })}
      </Badge>
    );
  }, [internalValue, sections, t]);

  return (
    <div className="flex flex-col space-y-1">
      <Popover
        open={isOpen}
        onOpenChange={() => {
          if (readonly) return;
          setIsOpen(!isOpen);
          onOpenChange?.(!isOpen);
        }}
      >
        <PopoverTrigger asChild className={cn("max-w-md", className)}>
          <SearchableSelectTriggerButton
            ref={ref}
            placeholder={placeholder}
            value={triggerDisplayValue}
            hasValue={triggerDisplayValue !== ""}
            error={error}
            variant={variant}
            isOpen={isOpen}
            size={size}
            multi={true}
            readonly={readonly}
          />
        </PopoverTrigger>
        <PopoverContent className="p-0 border border-gray-200 min-w-[var(--radix-popover-trigger-width)]">
          <Command shouldFilter={false}>
            <div className="border-b p-2 min-w-[var(--radix-popover-trigger-width)]">
              <TextField
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
                leftIcon="search"
                rightIcon="x"
                rightIconClick={() => setSearchValue("")}
              />
            </div>
            <CommandList
              onWheel={(e) => {
                e.stopPropagation();
              }}
              className="max-h-[218px] overflow-y-auto overscroll-y-auto overflow-x-hidden pink-scroll"
            >
              {isLoading ? (
                <LoadingRenderer />
              ) : filteredSections.length === 0 ? (
                <CommandEmpty>{noResultsText}</CommandEmpty>
              ) : (
                filteredSections.map((section) => (
                  <CommandGroup
                    key={`section-${section.id}`}
                    className="mt-1 mb-2"
                  >
                    {/* Section Header with Checkbox */}
                    <div
                      onClick={() => handleSectionToggle(section.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSectionToggle(section.id);
                        }
                      }}
                      className="flex items-center px-2 py-1 hover:bg-gray-50 cursor-pointer rounded w-full text-left"
                    >
                      <Checkbox
                        checked={getSectionCheckboxState(section.id)}
                        onCheckedChange={() => handleSectionToggle(section.id)}
                        className="mr-2"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Typography className="font-bold">
                        {section.title}
                      </Typography>
                    </div>

                    {/* Section Options */}
                    {section.options.map((option) => {
                      const selected = isOptionSelected(
                        section.id,
                        option.value,
                      );
                      return (
                        <CommandItem
                          key={`${section.id}-${String(option.value)}`}
                          value={String(option.value)}
                          onSelect={() =>
                            handleOptionToggle(section.id, option.value)
                          }
                          className={cn(
                            "font-normal flex items-center hover:cursor-pointer hover:bg-gray-50 text-gray-900 pr-3 pl-4 py-2 h-full",
                          )}
                        >
                          <div className="flex items-center w-full pl-6">
                            <Checkbox
                              checked={selected}
                              onCheckedChange={() =>
                                handleOptionToggle(section.id, option.value)
                              }
                              className="mr-2"
                            />
                            <div className="flex-1">
                              <TruncatedText>{option.label}</TruncatedText>
                              {option.description && (
                                <Typography
                                  variant="caption"
                                  className="text-gray-700 normal-case font-normal"
                                >
                                  {option.description}
                                </Typography>
                              )}
                            </div>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))
              )}
            </CommandList>
            <FooterRenderer />
          </Command>
        </PopoverContent>
      </Popover>

      {(error?.message || helperText) && (
        <InlineFeedbackGroup>
          {error?.message && (
            <InlineFeedback variant="critical" message={error.message} nested />
          )}
          {helperText && (
            <InlineFeedback variant="info" message={helperText} nested />
          )}
        </InlineFeedbackGroup>
      )}
    </div>
  );
};

export const TreeSearchableSelect = _TreeSearchableSelect;
